import { type DistrictSummary } from '../../lib/district';
import { useDistrictState } from './DistrictContext';

/**
 * 現在の地区情報サマリ。地区が解決済 (`status === 'ready'`) のときのみ値を返す。
 *
 * - DistrictProvider 配下 (`/m/:urlToken` route) で resolveDistrictByToken が成功した場合のみ非 null
 * - Provider 外 (`/` direct アクセス、解決前 loading、解決失敗 error、テスト等) では **null**
 *
 * 地区フォールバック根絶 (2026-06-08): 以前は未解決時に `PHASE2_FALLBACK_DISTRICT`
 * (id `'phase2-default'`) を返していたが、これが `districts/phase2-default/*` への
 * 誤読み書きの温床だったため撤去。呼び出し側は null を明示的に扱う
 * (地区依存ページは readiness で gate、子 component は `district?.x` で安全参照)。
 *
 * 旧 `contexts/DistrictContext.tsx` (L4 参照、docs/97 §3 sprint 2 stage 2 移植ログ) の
 * `useDistrict()` 表面 API を踏襲、内部実装を context 経由に切替。
 */
export type { DistrictSummary };

/**
 * 地区解決済を前提とする component / hook 専用の `useDistrict()` ラッパー。
 *
 * 地区フォールバック根絶 (2026-06-08): 地区依存ページ (AppHome / MapPage / AnalysisPage /
 * MapDownloadPage) は readiness gate を通過してから子 component (各 modal / Drawer / PostForm
 * 等) を render する。これら gate 配下の子は「地区は必ず ready」が保証されるため、
 * 毎回 `district?.x` で null 分岐するより本 hook で非 null を受け取る方が安全かつ簡潔。
 *
 * 万一 gate 外 (Provider 外 / 解決前) で呼ばれた場合は throw し、PageErrorBoundary が捕捉する
 * (旧来の phase2-default への暗黙フォールバックのように「誤地区で静かに動き続ける」事故を防ぐ)。
 */
export function useDistrictReady(): DistrictSummary {
  const district = useDistrict();
  if (!district) {
    throw new Error(
      'useDistrictReady(): 地区が未解決です。地区解決 (DistrictProvider ready) 後にのみ使用してください。',
    );
  }
  return district;
}

export function useDistrict(): DistrictSummary | null {
  const state = useDistrictState();
  // 地区「強い停止」Phase 1: 停止中の一般ユーザーには resolveDistrictByToken が地区データ系
  // フィールドなしの最小返却 (suspended:true) を返す。これを通常の地区サマリとして扱うと
  // 地区依存ページ (AppHome / MapPage 等) が描画されてしまうため、suspended は未解決 (null) 扱いにする。
  // EntryPage が districtState から suspended を検知して NonPublicScreen を出す。
  if (state?.status === 'ready' && state.district.suspended === true) {
    return null;
  }
  if (state?.status === 'ready') {
    return {
      id: state.district.districtId,
      name: state.district.districtName,
      mode: state.district.mode,
      // custom モード (B2) の利用者表示名 (組込モードは未設定 → DistrictHeader が固定ラベル使用)
      ...(state.district.modeLabel ? { modeLabel: state.district.modeLabel } : {}),
      analysisEnabled: state.district.analysisEnabled,
      analysisSiteUrl: state.district.analysisSiteUrl,
      analysisDisplayConfigId: state.district.analysisDisplayConfigId,
      aiAnalysisEnabled: state.district.aiAnalysisEnabled,
      aiAnalysisSettingsId: state.district.aiAnalysisSettingsId,
      // AI 対話設定 M4 ID 素通し (resolveDistrictByToken が aiDialogue.enabled 時のみ返す)。
      // IdeaWorkspace が aiDialogueSettings master を読み generateEcwDialogue へ渡す。
      aiDialogueSettingsId: state.district.aiDialogueSettingsId,
      // docs/29: 投稿詳細の AI 解析の観点 (追加の観点がある地区のみ)。
      ...(state.district.aiPostAnalyses ? { aiPostAnalyses: state.district.aiPostAnalyses } : {}),
      // Phase E (old2 投稿フロー移植): セッション未参加投稿可フラグを素通し (PostForm の session gating)。
      allowPostingWithoutSession: state.district.allowPostingWithoutSession,
      // user 第 12 弾 (GAP-U3 投稿エリア制限): callable から素通しで受領、PostForm が area check に使用
      postAreaCenter: state.district.postAreaCenter,
      postAreaRadiusM: state.district.postAreaRadiusM,
      // user 第 13 弾 (GAP-U7 QR 焼込み): RecoveryCodeModal で deep link 化に使用
      urlToken: state.district.urlToken,
      // Sprint BG-DataSettings 4: 地区別 userFields / postFields (UserFieldsForm / PostForm extraFields 駆動)
      ...(state.district.dataSettings ? { dataSettings: state.district.dataSettings } : {}),
      // Sprint BG-DataSettings 6+7 (GAP-A4 / U-H8 / U-H9 / L17): mapDisplay.colorRule 素通し
      ...(state.district.mapDisplay ? { mapDisplay: state.district.mapDisplay } : {}),
      // Sprint BG-DataSettings 6+7 (GAP-A5): defaultMapStyle 素通し
      ...(state.district.defaultMapStyle
        ? { defaultMapStyle: state.district.defaultMapStyle }
        : {}),
      // docs/22 GIS (要件1): gisEnabled 素通し (MapView 重畳 + Drawer GIS設定 出し分け)
      ...(state.district.gisEnabled !== undefined ? { gisEnabled: state.district.gisEnabled } : {}),
      // Task C: 投稿写真設定 (photoConfig) を素通し (PostForm / uploadPhoto が消費)。
      ...(state.district.photoConfig ? { photoConfig: state.district.photoConfig } : {}),
      // issue #24: AI 出力フォーマット要素の表示可否の地区上書きを素通し (PostDetailModal が消費)。
      ...(state.district.aiOutputDisplayOverrides
        ? { aiOutputDisplayOverrides: state.district.aiOutputDisplayOverrides }
        : {}),
    };
  }
  return null;
}
